import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

const initialState = {
  countries: [],
  loading: false,
  errors: [],
  creating: false
};

// The country list is mostly derived server-side from which countries currently have
// verified companies, but a super admin can also manually register a country ahead of
// time (createCountry below) — it then shows up here at 0 companies until a matching one
// is created/verified.
export const fetchCountries = createAsyncThunk(
  'countries/fetchCountries',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/country');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const uploadCountryImage = createAsyncThunk(
  'countries/uploadCountryImage',
  async ({ code, file }, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/country/${code}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      dispatch(fetchCountries());
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteCountryImage = createAsyncThunk(
  'countries/deleteCountryImage',
  async (code, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/country/${code}/image`);
      dispatch(fetchCountries());
      return code;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// Only the name is needed — the backend resolves the code and all translations
// automatically from ISO country data.
export const createCountry = createAsyncThunk(
  'countries/createCountry',
  async ({ name }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post('/country', { name });
      dispatch(fetchCountries());
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteCountry = createAsyncThunk(
  'countries/deleteCountry',
  async (code, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/country/${code}`);
      dispatch(fetchCountries());
      return code;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

const countriesSlice = createSlice({
  name: 'countries',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCountries.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.loading = false;
        state.countries = action.payload || [];
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.payload;
      })
      .addCase(createCountry.pending, (state) => {
        state.creating = true;
      })
      .addCase(createCountry.fulfilled, (state) => {
        state.creating = false;
      })
      .addCase(createCountry.rejected, (state, action) => {
        state.creating = false;
        state.errors = action.payload;
      });
  }
});

export default countriesSlice.reducer;
